import { useCallback, useEffect, useRef } from 'react'

import { FlowContextProps } from '@cash-web/shared-flow-data-access-context'

/**
 * A custom hook that manages focus behavior when navigating between journey panes and blocker views.
 *
 * This hook helps maintain a good user experience by:
 * 1. Tracking the last focused element when a blocker view changes
 * 2. Restoring focus when returning to an active journey pane
 * 3. Setting up event listeners to detect when focus enters the journey content
 *
 * @param contentRef - Reference to the journey content DOM element
 * @param active - The currently active journey (if any)
 * @returns An object containing the onBlockerChange callback
 */
export const useJourneyFlowInputFocus = ({ isActive, flow }: { isActive: boolean; flow?: FlowContextProps }) => {
  // Stores reference to the last focused element (like an input field)
  const focusElementRef = useRef<HTMLElement>()
  const contentRef = useRef<HTMLDivElement>(null)

  /**
   * Callback that captures the currently focused element when a blocker view changes.
   * This allows us to restore focus to the same element when returning to this journey.
   */
  const captureFocusedElement = useCallback(() => {
    if (document.activeElement !== document.body && document.activeElement !== document.documentElement) {
      focusElementRef.current = document.activeElement as HTMLElement
    }
  }, [])

  /**
   * Sets up a one-time focus detection listener on the journey content.
   * This helps track when a user interacts with elements in the journey.
   */
  useEffect(() => {
    const element = contentRef.current
    element?.addEventListener('focusin', captureFocusedElement, { once: true })
    return () => {
      element?.removeEventListener('focusin', captureFocusedElement, true)
    }
  }, [contentRef, captureFocusedElement])

  /**
   * When this journey becomes active, restore focus to the previously focused element.
   * This improves accessibility and user experience when navigating between journeys.
   */
  useEffect(() => {
    if (isActive) {
      if (focusElementRef.current) {
        focusElementRef.current?.focus()
      }
    }
  }, [isActive])

  /**
   * Capture the focused element when a flow is changing blocker views and not loading
   */
  useEffect(() => {
    if (!flow?.isLoading && flow?.active?.blockerData?.blocker != null) {
      captureFocusedElement?.()
    }
  }, [captureFocusedElement, flow?.isLoading, flow?.active?.blockerData?.blocker])

  return { captureFocusedElement, ref: contentRef }
}
